import { useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Inline subtask accordion. A task with subtasks can't be ticked off directly —
 * every subtask has to go first, then the parent completes itself.
 */
export default function SubtaskChecklist({ task, onTaskChanged, className }) {
  const [busy, setBusy] = useState(false);
  const subs = task.subtasks || [];
  const doneCount = subs.filter((s) => s.done).length;

  const toggle = async (st) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/tasks/${task.id}/subtasks/${st.id}`, { done: !st.done });
      const allDone = (data.subtasks || []).every((s) => s.done);
      if (allDone && data.status !== "done") {
        const { data: finished } = await api.put(`/tasks/${task.id}`, { status: "done" });
        toast.success("All subtasks done — task complete");
        onTaskChanged?.(finished);
      } else {
        onTaskChanged?.(data);
      }
    } catch { toast.error("Could not update subtask"); }
    finally { setBusy(false); }
  };

  const completeAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/subtasks/complete-all`);
      toast.success("All subtasks done — task complete");
      onTaskChanged?.(data);
    } catch { toast.error("Could not complete subtasks"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cn("mt-2 rounded-lg border border-border bg-muted/30 p-2 space-y-1", className)} data-testid={`subtask-accordion-${task.id}`}>
      <div className="flex items-center gap-2 px-1 pb-1">
        <ListChecks className="w-3 h-3 text-muted-foreground" />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {doneCount}/{subs.length} subtasks
        </span>
        {doneCount < subs.length && (
          <button
            onClick={completeAll}
            disabled={busy}
            data-testid={`subtask-complete-all-${task.id}`}
            className="ml-auto text-[10px] text-[hsl(var(--studio-primary))] hover:underline"
          >
            Complete all
          </button>
        )}
      </div>
      {subs.map((st) => (
        <button
          key={st.id}
          onClick={() => toggle(st)}
          disabled={busy}
          data-testid={`inline-subtask-${st.id}`}
          className="w-full flex items-start gap-2 text-left px-1 py-1 rounded hover:bg-accent/60"
        >
          {st.done
            ? <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--halo-teal))] mt-0.5 shrink-0" />
            : <Circle className="w-3.5 h-3.5 text-muted-foreground/50 mt-0.5 shrink-0" />}
          <span className={cn("text-xs leading-snug", st.done && "line-through text-muted-foreground")}>{st.title}</span>
        </button>
      ))}
    </div>
  );
}
